<template>
  <v-card>
    <Subheader dense title="Schema Fields" no-search>
      <v-btn color="green" small @click="addSchemaField">
        <v-icon class="mr-1">add</v-icon>
        {{ $t('Button.Add') }}
      </v-btn>
    </Subheader>
    <v-alert color="info" icon="info" :value="!hasFields">There are no metadata fields associated with this schema.
    </v-alert>
    <v-card-text>
      <v-flex xs12 v-if="systemDefiendFields && systemDefiendFields.length > 0">
        System Defined Fields
      </v-flex>
      <template v-for="(field, index) in systemDefiendFields">
        <v-layout row wrap fill-height class="tile-layout" :key="field.Id">          
          <v-flex xs12 md3 pa-1>
            <v-text-field box label="Field Name" hide-details v-model="field.Field" @input="modifySchemaField(field)" readonly>
            </v-text-field>
          </v-flex>
          <v-flex xs12 md3 pa-1>
            <v-select box label="Field Type" hide-details :items="fieldTypeNames" v-model="field.FieldType" @input="modifySchemaField(field)" readonly></v-select>
          </v-flex>
          <v-flex shrink class="pt-1">            
          </v-flex>
        </v-layout>
        <v-divider v-if="index + 1 < systemDefiendFields.length" :key="dividerKey(field.Id)"></v-divider>
      </template>
      <v-flex xs12 v-if="userDefinedFields && userDefinedFields.length > 0">
        User Defined Fields
      </v-flex>
      <template v-for="(field, index) in userDefinedFields">        
        <v-layout row wrap fill-height class="tile-layout" :key="field.Id">          
          <v-flex xs12 md3 pa-1>
            <v-text-field box label="Field Name" hide-details v-model="field.Field" @input="modifySchemaField(field)" :disabled="(field.SystemDefined)">
            </v-text-field>
          </v-flex>
          <v-flex xs12 md3 pa-1>
            <v-select box label="Field Type" hide-details :items="fieldTypeNames" v-model="field.FieldType" @input="modifySchemaField(field)" :disabled="(field.SystemDefined)"></v-select>
          </v-flex>
          <v-flex shrink class="pt-1">
            <v-btn fab small outline color="error" @click="removeSchemaField(field.Id)" :disabled="(field.SystemDefined)">
              <v-icon>close</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
        <v-divider v-if="index + 1 < userDefinedFields.length" :key="dividerKey(field.Id)"></v-divider>
      </template>
    </v-card-text>
    <v-card-actions>
      <v-btn color="success" @click="onSubmitForm">
        <v-icon>done</v-icon>
        {{ finishedButtonText }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  
  import MetadataUtil from '@/components/mixins/MetadataUtil'
  import ReactivityUtil from '@/components/mixins/ReactivityUtil'
  import AppConfirmationModal from '@/components/generic/AppConfirmationModal'
  
  const authenticate = createNamespacedHelpers('authenticate')

  export default {
    components: {      
      'app-confirmation-modal': AppConfirmationModal,
    },
    mixins: [MetadataUtil, ReactivityUtil],
    props: {
      schema: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        formSchema: {},
        confirmDialog: false,
        confirmDeleteMessage : '',
      }
    },
    mounted() {
      this.setMetadataSchemaData()
    },
    computed: {
      ...authenticate.mapGetters([
        'isAuthed',
        'isSystemAdmin',
      ]),
      hasSystemAdmin() {
        return this.isAuthed && this.isSystemAdmin
      },
      addedFields() {
        return this.formSchema.Fields.filter(f => {
          if (typeof f.Id === 'number') {
            return false
          }
          return f.Id.includes('add')
        })
      },
      nextAddId() {
        let addedIds = this.addedFields.map(f =>
          Number.parseInt(f.Id.split('-')[1], 10)
        )
        let num
        if (addedIds.length == 0) {
          num = 0
        } else {
          num = addedIds.reduce((a, b) => (a > b ? a : b), 0) + 1
        }
        return `add-${num}`
      },
      hasFields() {
        return (
          this.formSchema &&
          this.formSchema.Fields &&
          this.formSchema.Fields.length > 0
        )
      },
      formHasChanged() {
        let that = this
        if (this.formSchema.Fields == null) {
          return false
        }
        if (this.schema.Fields.length != this.formSchema.Fields.length) {
          return true
        }
        return this.schema.Fields.reduce((hasChanged, field) => {
          let formSchemaField = that.formSchema.Fields.find(x => x.Id == field.Id)
          if (formSchemaField == null) {
            return true
          }
          if (field.Field != formSchemaField.Field) {
            return true
          }
          if (field.FieldType != formSchemaField.FieldType) {
            return true
          }
          return hasChanged
        }, false)
      },
      finishedButtonText() {
        return this.formHasChanged ? this.$t('Button.Save') : this.$t('Button.Done')
      },
      systemDefiendFields() {
        if(this.formSchema && this.formSchema.Fields) {
          return this.formSchema.Fields.filter(x => x.SystemDefined)
        }
        return null        
      },
      userDefinedFields() {
        if(this.formSchema && this.formSchema.Fields) {
          return this.formSchema.Fields.filter(x => !x.SystemDefined)
        }
        return null        
      },
      
    },
    watch: {
      schema() {
        this.setMetadataSchemaData()
      },
    },
    methods: {      
      onSubmitForm() {
        if (this.formHasChanged) {
          this.emitSubmitEvent()
        } else {
          this.emitCloseEvent()
        }
      },
      clearForm() {},
      addSchemaField() {
        let id = this.nextAddId
        let field = {
          Id: id,
          Field: '',
          FieldType: 'String',
          SystemDefined: false,
        }
        this.reactivePush(this.formSchema.Fields, field)
        this.emitChangeEvent()
      },
      removeSchemaField(id) {
        let index = this.formSchema.Fields.findIndex(x => x.Id == id)
        if (index >= 0) {
          this.$set(
            this.formSchema,
            'Fields',
            this.arrayOmitIndex(this.formSchema.Fields, index)
          )
        }
        this.emitChangeEvent()
      },

      modifySchemaField() {
        this.emitChangeEvent()
      },
      handleAddSchemaField(id, field) {
        this.$set(this.logActions.add, id, {
          Id: id,
          Field: field.Field,
          FieldType: field.FieldType,
          SystemDefined: false,
        })
      },
      handleRemoveSchemaField(id) {
        if (this.isAddId(id)) {
          this.$delete(this.logActions.add, id)
        } else {
          this.$set(this.logActions.remove, id, true)
        }
      },
      handleModifySchemaField() {},
      isAddId(id) {
        if (typeof id === 'number') return false
        return id.includes('add')
      },
      dividerKey(id) {
        return `divider-${id}`
      },
      setMetadataSchemaData() {        
        this.formSchema = JSON.parse(JSON.stringify(this.schema))
      },
      emitChangeEvent() {
        this.$emit('change', this.formSchema)
      },
      emitSubmitEvent() {
        this.$emit('submit', this.formSchema)
      },
      emitCloseEvent() {
        this.$emit('close')
      },
    },
  }
</script>

<style lang="stylus" scoped>
  div.tile-layout {
    width: 100%;
  }

  div.tile-layout>>>div.body-1 {
    /*
	justify-content: center;
	display: flex;
	flex-direction: column;
	height: 100%;
	*/
  }

  >>>div.v-list__tile {
    max-height: 300px;
    height: auto;
  }
</style>
